import { useEffect, useState } from "react";
import { GenericButton } from "components/Elements";
import Input from "components/Elements/Inputv2";
import Select from "components/Elements/Selectv2";
import { useNavigate } from "react-router-dom";
import ApiService from "services/api.service";
import { ApiConfig } from "../../../constants";

interface Agency {
  id: number;
  agencyName: string;
  agencyCode: string;
}

const NO_AGENCY = "";

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-[13px] font-medium text-themed-text">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </div>
      {children}
    </div>
  );
}

const NamePerson = () => {
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [birthday, setBirthday] = useState("");
  const [selectedAgency, setSelectedAgency] = useState<string>(NO_AGENCY);

  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [agenciesLoaded, setAgenciesLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await ApiService.get<Agency[]>({
          url: "/ricochet-data/agencies",
        });
        if (!cancelled) setAgencies(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setAgencies([]);
      } finally {
        if (!cancelled) setAgenciesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async () => {
    if (!email.trim()) return;
    setIsSubmitting(true);
    try {
      const payload: Record<string, string> = {
        email: email.trim(),
      };
      if (firstName.trim()) payload.firstName = firstName.trim();
      if (lastName.trim()) payload.lastName = lastName.trim();
      if (phone.trim()) payload.phone = phone.trim();
      if (birthday) payload.birthday = birthday;
      if (selectedAgency) payload.agency = selectedAgency;

      const { data } = await ApiService.post({
        url: `${ApiConfig.contactCreate}`,
        options: payload,
      });
      if (data) navigate(`/person/${data}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const agencyOptions = [
    { key: NO_AGENCY, title: "— No agency —" },
    ...agencies.map((a) => ({ key: a.agencyName, title: a.agencyName })),
  ];

  return (
    <div className="flex items-start justify-center pt-[18px]">
      <div className="bg-themed-card rounded-3xl w-full max-w-[560px] p-5">
        <div className="text-[20px] font-semibold leading-[28px] mb-5">
          Create Contact
        </div>

        <div className="flex flex-col gap-4">
          <Field label="First name">
            <Input
              value={firstName}
              onChange={setFirstName}
              placeholder="First name"
              wrapperClassName="!max-w-full w-full"
              className="w-full"
            />
          </Field>
          <Field label="Last name">
            <Input
              value={lastName}
              onChange={setLastName}
              placeholder="Last name"
              wrapperClassName="!max-w-full w-full"
              className="w-full"
            />
          </Field>
          <Field label="Email" required>
            <Input
              value={email}
              onChange={setEmail}
              placeholder="name@example.com"
              wrapperClassName="!max-w-full w-full"
              className="w-full"
            />
          </Field>
          <Field label="Phone">
            <Input
              value={phone}
              onChange={setPhone}
              placeholder="+15555550123"
              wrapperClassName="!max-w-full w-full"
              className="w-full"
            />
          </Field>
          <Field label="Birthday">
            <input
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              className="w-full border border-themed-text/20 bg-themed-background text-themed-text px-2.5 py-2 text-[14px] leading-[20px] rounded"
            />
          </Field>
          <Field label="Agency">
            <Select
              value={selectedAgency}
              options={agencyOptions}
              onChange={(v) => setSelectedAgency(v)}
              placeholder={
                agenciesLoaded && agencies.length === 0
                  ? "No agencies available for this workspace"
                  : "Select an agency"
              }
              disabled={agenciesLoaded && agencies.length === 0}
              id="agency-select"
              buttonClassName="w-full"
              buttonInnerWrapperClassName="w-full"
              panelClassName="w-full"
            />
          </Field>
        </div>

        <div className="mt-6 flex justify-end">
          <GenericButton
            onClick={handleSubmit}
            style={{ maxWidth: "200px" }}
            disabled={!email.trim() || isSubmitting}
            id="create-person-modal-button"
          >
            Create Contact
          </GenericButton>
        </div>
      </div>
    </div>
  );
};

export default NamePerson;
